import React, { useState, useRef } from "react";
import { MailIcon, CheckCircle2, Loader2, KeyIcon, HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/app/components/ui/dialog.jsx";
import { Button } from "@/app/components/ui/button.js";
import { Input } from "@/app/components/ui/input.jsx";
import { useCompanyClaim } from "@/app/hooks/useCompanyClaim.js";

export const ClaimModal = ({ open, onOpenChange, companyId, companyName, initialEmail, initialStep }) => {
  const [step, setStep] = useState(initialStep || "email");
  const [email, setEmail] = useState(initialEmail || "");
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const { requestClaimOTP, verifyClaimOTP, isLoading } = useCompanyClaim();

  const handleSendVerification = async () => {
    if (!email) return;

		const payload = {
			"companyId":companyId,
			"email":email
		}
    
    const success = await requestClaimOTP(payload);
		if (success) {
			setStep("verification");
		}
  };

  const handleVerify = async () => {
    const code = verificationCode.join("");
    if (code.length !== 6) return;

		const payload = {
			"companyId":companyId,
			"email":email,
			"otp":code
		}
    
    const success = await verifyClaimOTP(payload);
		if (success) {
			setStep("success");
		}
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset after animation completes
    setTimeout(() => {
      setStep(initialStep || "email");
      setEmail(initialEmail || "");
      setVerificationCode(["", "", "", "", "", ""]);
    }, 300);
  };
  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      value = value[0];
    }

    if (!/^\d*$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Autofocus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...verificationCode];
    pastedData.split("").forEach((char, index) => {
      if (index < 6) {
        newCode[index] = char;
      }
    });
    setVerificationCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex((val) => !val);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[800px] bg-white p-12">
        {step === "email" && (
          <div className="flex flex-col items-center gap-8">
            <div className="w-[60px] h-[60px] bg-[#e6f3ff] rounded-3xl flex items-center justify-center">
              <MailIcon className="w-8 h-8 text-[#0a6fe2]" strokeWidth={1.5} />
            </div>

            <div className="flex flex-col gap-3 text-center">
              <h2 className="text-[32px] font-bold text-[#1e1e1e] [font-family:'Plus_Jakarta_Sans',Helvetica] leading-tight">
                Claim {companyName}
              </h2>
              <p className="text-base text-[#6c757d] [font-family:'Inter',Helvetica] leading-relaxed">
                Enter your company email to verify that you work at this company.
              </p>
            </div>

            <div className="flex flex-col gap-2 w-full">
              <label className="text-base font-semibold text-[#1e1e1e] [font-family:'Inter',Helvetica]">
                Company Email
              </label>
              <div className="relative">
                <Input
                  type="email"
                  placeholder="Enter company email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-14 px-4 pr-12 bg-white rounded-xl border border-[#dee2e6] [font-family:'Inter',Helvetica] text-base text-[#495057] placeholder:text-[#adb5bd] focus-visible:ring-2 focus-visible:ring-[#0a6fe2] focus-visible:border-[#0a6fe2]"
                  disabled={isLoading}
                />
                <HelpCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#adb5bd]" />
              </div>
            </div>

            <div className="flex items-center gap-4 w-full pt-4">
              <Button
                onClick={handleClose}
                variant="outline"
                disabled={isLoading}
                className="flex-1 h-14 bg-white text-[#1e1e1e] rounded-xl [font-family:'Inter',Helvetica] font-semibold text-base hover:bg-[#f8f9fa]"
								style={{border: "1px solid #dee2e6"}}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendVerification}
                disabled={!email || isLoading}
                className="flex-1 h-14 bg-primary-700 hover:bg-primary-700/90 text-white rounded-xl shadow-none [font-family:'Inter',Helvetica] font-semibold text-base disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Verification Code"
                )}
              </Button>
            </div>
          </div>
        )}

        {step === "verification" && (
          <div className="flex flex-col items-center gap-8">
            <div className="w-[60px] h-[60px] bg-[#e6f3ff] rounded-3xl flex items-center justify-center">
              <KeyIcon className="w-8 h-8 text-[#0a6fe2]" strokeWidth={1.5} />
            </div>

            <div className="flex flex-col gap-3 text-center">
              <h2 className="text-[32px] font-bold text-[#1e1e1e] [font-family:'Plus_Jakarta_Sans',Helvetica] leading-tight">
                Enter verification code
              </h2>
              <p className="text-base text-[#6c757d] [font-family:'Inter',Helvetica] leading-relaxed">
                We've sent a 6-digit code to <span className="font-semibold text-[#1e1e1e]">{email}</span>
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 w-full">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isLoading}
                  className="w-16 h-16 text-center text-xl font-semibold bg-white border-2 border-[#dee2e6] rounded-2xl [font-family:'Inter',Helvetica] text-[#1e1e1e] focus:outline-none focus:ring-2 focus:ring-[#0a6fe2] focus:border-[#0a6fe2] disabled:opacity-50"
                />
              ))}
            </div>

            <p className="text-base text-[#6c757d] [font-family:'Inter',Helvetica]">
              Didn't get a code?{" "}
              <button
                onClick={handleSendVerification}
                disabled={isLoading}
                className="text-[#0a6fe2] underline hover:text-[#0856b3] font-medium"
              >
                Click to resend
              </button>
              .
            </p>

            <div className="flex items-center gap-4 w-full pt-4">
              <Button
                onClick={handleClose}
                variant="outline"
                disabled={isLoading}
                className="flex-1 h-14 bg-white border-[#dee2e6] text-[#1e1e1e] rounded-xl [font-family:'Inter',Helvetica] font-semibold text-base hover:bg-[#f8f9fa]"
								style={{border: "1px solid #dee2e6"}}
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerify}
                disabled={verificationCode.join("").length !== 6 || isLoading}
                className="flex-1 h-14 bg-primary-700 hover:bg-primary-700/90 text-white rounded-xl shadow-none [font-family:'Inter',Helvetica] font-semibold text-base disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify & Continue"
                )}
              </Button>
            </div>

            <button
              onClick={() => setStep("email")}
              disabled={isLoading}
              className="text-base text-[#6c757d] hover:text-[#1e1e1e] [font-family:'Inter',Helvetica] font-medium underline"
            >
              Use a different email
            </button>
          </div>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center gap-8 py-4">
            <div className="w-[60px] h-[60px] bg-[#e6f3ff] rounded-3xl flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#0a6fe2]" strokeWidth={1.5} />
            </div>

            <div className="flex flex-col gap-4 text-center">
              <h2 className="text-[32px] font-bold text-[#1e1e1e] [font-family:'Plus_Jakarta_Sans',Helvetica] leading-tight">
                You're verified!
              </h2>
              <p className="text-base text-[#6c757d] [font-family:'Inter',Helvetica] leading-relaxed">
                Congratulations! You now have full control of your company page on Dproz.
              </p>
            </div>

            <div className="flex items-center gap-4 p-6 bg-[#f8f9fa] rounded-2xl w-full">
              <div className="w-16 h-16 bg-[#0a6fe2] rounded-full flex items-center justify-center flex-shrink-0">
                {/* <img
                  src="https://c.animaapp.com/mkfndw4v3NChDU/img/logo-grayscale.png"
                  alt="ABC Logistics"
                  className="w-12 h-12 object-contain"
                /> */}
                {/* Placeholder for company logo image, as the original path is external */}
                <span className="text-white text-xl font-bold">{companyName?.substring(0, 3)?.toUpperCase()}</span>
              </div>
              <div className="flex flex-col items-start gap-1 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-[#1e1e1e] [font-family:'Inter',Helvetica]">
                    {companyName}
                  </h3>
                  <div className="flex items-center gap-1 px-2 py-1 bg-[#e6f3ff] rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-[#0a6fe2]" />
                    <span className="text-sm font-semibold text-[#0a6fe2] [font-family:'Inter',Helvetica]">
                      Verified
                    </span>
                  </div>
                </div>
                <p className="text-base text-[#6c757d] [font-family:'Inter',Helvetica]">
									{email}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-0 w-full pt-4">
              <Button
                onClick={handleClose}
                className="w-full h-14 text-white rounded-xl shadow-none [font-family:'Inter',Helvetica] font-semibold text-base"
								style={{backgroundColor: "#0a6fe2", hoverBackgroundColor: "#0856b3"}}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
